import React, { useEffect, useState } from "react";

/* Route-level code splitting that survives hydration.
 *
 * The build snapshots each route in a real browser (scripts/prerender.py), so
 * the HTML being hydrated is a browser's serialisation of a live DOM. It
 * contains no Suspense boundary markers — React writes those as `<!--$-->` /
 * `<!--/$-->` comment nodes around a boundary's content, and a browser DOM has
 * none to give. React hydrates a Suspense fiber by looking for that comment,
 * so a `<Suspense>` anywhere in the tree fails to hydrate whether or not it
 * ever suspends. That was the whole of the residual recoverable mismatch
 * (React #418 + #423, twice per route, on every route including the
 * un-split Home): one boundary, directly under the root.
 *
 * So there is no Suspense and no React.lazy here. A route holds its resolved
 * component in module state; index.js resolves the current route's chunk
 * before hydration starts, so the first render is synchronous with nothing to
 * suspend on. A client-side navigation to a not-yet-loaded chunk renders null
 * for the round trip and re-renders when the import lands — the same gap the
 * `fallback={null}` boundary produced, without the boundary. */
function splitRoute(factory) {
  let Resolved = null;
  let pending = null;

  function load() {
    if (Resolved) return Promise.resolve(Resolved);
    if (!pending) {
      pending = factory().then((mod) => {
        Resolved = mod.default;
        return mod.default;
      });
    }
    return pending;
  }

  function Route(props) {
    /* Function components must be stored through an updater, or useState
     * would call them as one. */
    const [Comp, setComp] = useState(() => Resolved);

    useEffect(() => {
      if (Comp) return undefined;
      let alive = true;
      load().then((C) => {
        if (alive) setComp(() => C);
      });
      return () => {
        alive = false;
      };
    }, [Comp]);

    return Comp ? <Comp {...props} /> : null;
  }

  Route.preload = load;

  return Route;
}

export const PrivacyPolicy = splitRoute(() => import("./Components/legal/PrivacyPolicy"));
export const TermsOfService = splitRoute(() => import("./Components/legal/TermsOfService"));
export const BlogIndex = splitRoute(() => import("./Components/blog/BlogIndex"));
export const BlogPost = splitRoute(() => import("./Components/blog/BlogPost"));

/* Longest-prefix match, so /blog/<slug> resolves to BlogPost and /blog to the
 * index. Returns null for "/" — Home is not split. */
export function routeFor(pathname) {
  const path = pathname.replace(/\/+$/, "") || "/";
  if (path === "/privacy") return PrivacyPolicy;
  if (path === "/terms") return TermsOfService;
  if (path === "/blog") return BlogIndex;
  if (path.startsWith("/blog/")) return BlogPost;
  return null;
}
